<template>
  <div ref="wrapperEl" class="content-mode-wrapper">
    <!-- 🔷 EXTERNAL WINDOW MODE: sidecar slot in external window -->
    <div v-if="isExternalWindow" class="content-mode-sidecar-only">
      <slot name="sidecar" :contentMode :isExternalWindow :slotName="'sidecar'" />
    </div>

    <!-- 🔲 STANDARD MODE: Only main slot -->
    <div v-else-if="contentMode === 'standard'" class="content-mode-main-only">
      <slot name="main" :contentMode :isExternalWindow :slotName="'main'" />
    </div>

    <!-- ⚫ SPLIT VIEW MODE: Both main and sidecar -->
    <div v-else-if="contentMode === 'split'" class="content-mode-split">
      <div v-if="slotPosition === 'main-panel' || !slotPosition" class="content-mode-split-main">
        <slot name="main" :contentMode :isExternalWindow :slotName="'main'" />
      </div>
      <div v-if="slotPosition === 'split-panel'" class="content-mode-split-sidecar">
        <slot name="sidecar" :contentMode :isExternalWindow :slotName="'sidecar'" />
      </div>
    </div>

    <!-- 🔷 EXTERNAL WINDOW MODE, main slot in application -->
    <div v-else class="content-mode-main-only">
      <slot name="main" :contentMode :isExternalWindow :slotName="'main'" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import type { ContentMode } from '@/stores/contentmode';
import { useContentModeStore } from '@/stores/contentmode';
import { useContentModeStateStore } from '@/stores/contentmodestate';
import { buildContentModeKey } from '@/utils/contentModeKey';

/**
 * Props for ContentModeWrapper
 */
interface Props {
  /**
   * Optional: Override the automatic key detection
   * If provided, this key will be used for storing/retrieving content mode
   * If not provided, uses the root entity from the URL path
   * Example: contentModeKey="nebenkostenabrechnung"
   */
  contentModeKey?: string;

  /**
   * Optional: Force a specific content mode, bypassing the stored preference
   * Useful for modals where split-view doesn't make sense
   * Example: :forceMode="inModal ? 'standard' : undefined"
   */
  forceMode?: ContentMode;
}

const props = defineProps<Props>();
const route = useRoute();
const contentModeStore = useContentModeStore();
const contentModeStateStore = useContentModeStateStore();
const wrapperEl = ref<HTMLElement | null>(null);

// Get current entity from route path (fallback if contentModeKey not provided)
const currentEntity = computed(() => {
  const segments = route.path.split('/').filter(Boolean);
  return segments[0] || 'default';
});

// Determine which key to use (prop override or path-based)
const effectiveContentModeKey = computed(() => {
  const routeKey = buildContentModeKey({
    mainComponent: route.params.mainComponent as string,
    subComponent: route.params.subComponent as string,
    subComponentIdentifier: route.params.subComponentIdentifier as string,
  });

  return routeKey !== 'default' ? routeKey : props.contentModeKey || currentEntity.value;
});

// Get contentMode - use forceMode if provided, otherwise read from store
const contentMode = computed<ContentMode>(() => {
  // If forceMode is set, use it instead of store value
  if (props.forceMode) {
    return props.forceMode;
  }
  return contentModeStore.getContentModeForRoute(effectiveContentModeKey.value);
});

// Check if external window
const isExternalWindow = computed(() => {
  return route.meta.isExternalWindow === true;
});

// 📍 Read slot position from parent FocusArea data-slot attribute
const slotPosition = computed(() => {
  if (!wrapperEl.value) return null;

  let current = wrapperEl.value as HTMLElement | null;
  while (current) {
    const slot = current.getAttribute('data-slot');
    if (slot === 'main-panel' || slot === 'split-panel') {
      return slot;
    }
    current = current.parentElement;
  }

  return null;
});

// 📌 Signal when component mounts/unmounts
onMounted(() => {
  // ⚠️ IMPORTANT: Only set activeContentModeKey if this is a TRULY nested component
  // Check: Different key from root entity AND we're in split-panel with split mode
  const isReallyNested =
    effectiveContentModeKey.value !== currentEntity.value &&
    slotPosition.value === 'split-panel' &&
    contentMode.value === 'split';

  if (isReallyNested) {
    contentModeStore.setActiveContentModeKey(effectiveContentModeKey.value);
  }

  // Save the current route for external windows
  contentModeStateStore.setCurrentContentRoute(route.path, route.query as Record<string, any>);
  contentModeStateStore.setWrapperMounted(true);
});

onUnmounted(() => {
  contentModeStateStore.setWrapperMounted(false);
});
</script>

<style scoped lang="css">
.content-mode-wrapper {
  width: 100%;
  height: 100%;
}

.content-mode-main-only {
  width: 100%;
  height: 100%;
}

.content-mode-sidecar-only {
  width: 100%;
  height: 100%;
}

.content-mode-split {
  display: flex;
  gap: 0;
  width: 100%;
  height: 100%;
}

.content-mode-split-main {
  flex: 1;
  overflow: auto;
  /* no width set: flex will handle it */
}

.content-mode-split-sidecar {
  flex: 1;
  overflow: auto;
  /* no width set: flex will handle it */
}

.content-mode-fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
